import React, { useState } from 'react';
import { Linkedin, Heart, Facebook } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import CookiePreferences from '../Privacy/CookiePreferences';
import { buildUrl } from '../../utils/urlUtils';
import footerMedia from '../../config/footerMedia.json';

interface FooterProps {
  onNavigate?: (page: string) => void;
}

export const Footer: React.FC<FooterProps> = ({ onNavigate }) => {
  const { t } = useTranslation('common');
  const [showCookiePreferences, setShowCookiePreferences] = useState(false);

  const navHref = (page: string) => {
    const path = page.endsWith('/') ? `/${page}` : `/${page}/`;
    return buildUrl(path);
  };

  const linkClass =
    'footer-link text-gray-600 dark:text-gray-400 theme-black:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 theme-black:hover:text-primary-400 text-sm transition-colors duration-200';

  const headingClass =
    'text-sm font-semibold text-yellow-400 uppercase tracking-wider mb-4';

  return (
    <footer className="relative z-10 overflow-hidden bg-white dark:bg-gray-950 theme-black:bg-transparent border-t border-gray-200 dark:border-white/10 theme-black:border-white/10 transition-colors duration-300">
      <img
        src={footerMedia.darkBackground}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        className="pointer-events-none absolute inset-0 hidden h-full w-full object-cover object-center grayscale dark:block theme-black:hidden"
        style={{ opacity: footerMedia.darkImageOpacity }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 hidden bg-gradient-to-b from-black/65 via-black/55 to-black/75 dark:block theme-black:hidden"
      />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Top: Logo + Slogan — identical to sister sites */}
        <div className="flex flex-col items-center text-center mb-12">
          <div className="flex items-center gap-3 mb-4">
            <img
              src="/logokomby.webp"
              alt="KOMBY"
              className="h-10 w-auto"
              loading="lazy"
            />
          </div>
          <p className="text-lg font-bold text-gray-900 dark:text-white theme-black:text-white">
            {t('footer.slogan', 'Make it Easy, whatever your "it" is.')}
          </p>
          <p className="text-sm text-gray-500 dark:text-white/60 theme-black:text-white/60 mt-1">
            {t('footer.metasaas', 'The first MetaSaaS in the world.')}
          </p>
          <div className="flex space-x-3 sm:space-x-4 mt-5">
            <a
              href="https://facebook.com/komby.io"
              className="text-gray-500 theme-black:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 theme-black:hover:text-primary-400 transition-colors duration-200"
              aria-label="Facebook"
            >
              <Facebook className="h-5 w-5" />
            </a>
            <a
              href="https://tiktok.com/@komby.io"
              className="text-gray-500 theme-black:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 theme-black:hover:text-primary-400 transition-colors duration-200"
              aria-label="TikTok"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
            </a>
            <a
              href="https://www.linkedin.com/company/kombyio/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 theme-black:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 theme-black:hover:text-primary-400 transition-colors duration-200"
              aria-label="LinkedIn"
            >
              <Linkedin className="h-5 w-5" />
            </a>
          </div>
        </div>

        {/* Same columns / links / targets as before */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8 mb-8">
          {/* Support */}
          <div className="min-w-0">
            <h4 className={headingClass}>
              {t('footer.support', 'Support')}
            </h4>
            <ul className="space-y-2">
              <li>
                <a
                  href={navHref('help')}
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate?.('help');
                  }}
                  className={linkClass}
                >
                  {t('footer.supportCenter', 'Support Center')}
                </a>
              </li>
              <li>
                <a
                  href={navHref('contact')}
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate?.('contact');
                  }}
                  className={linkClass}
                >
                  {t('footer.contact', 'Contact')}
                </a>
              </li>
              <li>
                <a
                  href={navHref('pricing')}
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate?.('pricing');
                  }}
                  className={linkClass}
                >
                  {t('footer.pricing', 'Pricing')}
                </a>
              </li>
              <li>
                <a
                  href={navHref('compare')}
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate?.('compare');
                  }}
                  className={linkClass}
                >
                  {t('footer.compare', 'Compare')}
                </a>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div className="min-w-0">
            <h4 className={headingClass}>
              {t('footer.company', 'Company')}
            </h4>
            <ul className="space-y-2">
              <li>
                <a
                  href={navHref('about')}
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate?.('about');
                  }}
                  className={linkClass}
                >
                  {t('footer.about', 'About')}
                </a>
              </li>
              <li>
                <a href={navHref('resto')} className={linkClass}>
                  {t('footer.restaurants', 'Restaurant Software')}
                </a>
              </li>
              <li>
                <a href={navHref('office')} className={linkClass}>
                  {t('footer.offices', 'Office')}
                </a>
              </li>
              <li>
                <a href={navHref('nightclub')} className={linkClass}>
                  {t('footer.nightlife', 'Nightlife')}
                </a>
              </li>
              <li>
                <a
                  href={navHref('music')}
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate?.('music');
                  }}
                  className={linkClass}
                >
                  🎵 {t('footer.music', 'Komby')}
                </a>
              </li>
              <li>
                <a
                  href={navHref('blog')}
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate?.('blog');
                  }}
                  className={linkClass}
                >
                  {t('footer.blog', 'Blog')}
                </a>
              </li>
              <li>
                <a
                  href={navHref('press')}
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate?.('press');
                  }}
                  className={linkClass}
                >
                  {t('footer.press', 'Press')}
                </a>
              </li>
              <li>
                <a
                  href={navHref('team')}
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate?.('team');
                  }}
                  className={linkClass}
                >
                  {t('footer.team', 'Team')}
                </a>
              </li>
              <li>
                <a
                  href={navHref('careers')}
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate?.('careers');
                  }}
                  className={linkClass}
                >
                  {t('footer.careers', 'Careers')}
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div className="min-w-0">
            <h4 className={headingClass}>
              {t('footer.legal', 'Legal')}
            </h4>
            <ul className="space-y-2">
              <li>
                <a
                  href={navHref('cgv')}
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate?.('cgv');
                  }}
                  className={linkClass}
                >
                  {t('footer.terms', 'Terms')}
                </a>
              </li>
              <li>
                <a
                  href={navHref('rgpd')}
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate?.('rgpd');
                  }}
                  className={linkClass}
                >
                  {t('footer.privacy', 'Privacy')}
                </a>
              </li>
              <li>
                <a
                  href={navHref('ai-policy')}
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigate?.('ai-policy');
                  }}
                  className={linkClass}
                >
                  {t('footer.aiPolicy', 'AI Policy')}
                </a>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => setShowCookiePreferences(true)}
                  className={linkClass}
                >
                  {t('footer.cookiePreferences', 'Cookie preferences')}
                </button>
              </li>
            </ul>
          </div>

          {/* Ecosystem — crawlable sister sites + Capital */}
          <div className="min-w-0">
            <h4 className={headingClass}>
              {t('footer.ecosystem', 'Ecosystem')}
            </h4>
            <ul className="space-y-2 mb-4">
              <li>
                <a
                  href="https://komby.restaurant/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkClass}
                >
                  {t('footer.sisterRestaurant', 'Restaurant')}
                </a>
              </li>
              <li>
                <a
                  href="https://komby.work/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkClass}
                >
                  {t('footer.sisterWork', 'Work')}
                </a>
              </li>
              <li>
                <a
                  href="https://komby.music/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkClass}
                >
                  {t('footer.sisterMusic', 'Music')}
                </a>
              </li>
              <li>
                <a
                  href="https://kombybrowser.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkClass}
                >
                  {t('footer.sisterCapsule', 'Capsule')}
                </a>
              </li>
              <li>
                <a
                  href="https://kombycapital.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkClass}
                >
                  {t('footer.sisterCapital', 'Capital')}
                </a>
              </li>
            </ul>
            <a
              href="https://kombycapital.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-yellow-400 hover:bg-yellow-300 text-gray-900 text-sm font-semibold transition-colors duration-200 shadow-sm"
            >
              {t('footer.investCta', 'invest inside Komby')}
            </a>
          </div>
        </div>

        {/* Bottom: AI then Made with + Patent — stacked on mobile, row on desktop */}
        <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-800 theme-black:border-white/10">
          <p className="text-center text-blue-800 dark:text-blue-300 theme-black:text-blue-300 text-[10px] leading-tight mb-4 sm:whitespace-nowrap">
            {t('footer.aiTransparency1', 'AI assistant: Some email responses may be assisted by AI.')}
            {' '}
            {t('footer.aiTransparency2', 'You can request a human at any time in email support.')}
          </p>
          <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:items-center sm:justify-between sm:gap-6">
            <div className="text-gray-600 dark:text-gray-400 theme-black:text-gray-300 text-sm text-center sm:text-start">
              {t('footer.madeWith', 'Fait avec')} <Heart className="inline w-4 h-4 text-red-500" /> {t('footer.andTech', 'et de la technologie')}
            </div>
            <div className="text-sm text-center sm:text-end">
              <p className="text-gray-600 dark:text-gray-400 theme-black:text-gray-300">
                © {new Date().getFullYear()} {t('footer.copyright', 'Komby. Tous droits réservés.')}
              </p>
              <p className="text-yellow-400">
                {t('footer.patent', 'Brevet en cours')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {showCookiePreferences && (
        <CookiePreferences isOpen={showCookiePreferences} onClose={() => setShowCookiePreferences(false)} />
      )}
    </footer>
  );
};
